<template>
  <div id="app">
    <vc-nav-drawer color="white" tag="nav" v-resize="resize" :dark="false" application :fixed="fixed" :temporary="temporary" v-model="open">
      <vc-container :class="['vuecidity-app-menu']">
        <h1 :class="['headline', `font-${categoryColor}`, 'align-center', 'my-8']" v-html="title" />
        <div v-if="version" :class="['align-center']">
          <router-link to="/getting-started/quick-start" :class="['font-grey', 'underline-off']">version {{ version }}</router-link>
        </div>
        <vc-avatar
          tile
          v-ripple
          tag="router-link"
          size="128"
          href="/"
          :src="`data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1017.302 850.39'><path d='M915.066 237.155l-150.179 2.015a25.18 25.18 0 0 0-21.294 12.292L508.651 645.925 273.708 251.462a25.18 25.18 0 0 0-21.293-12.292l-150.179-2.015-.345-.002c-13.747 0-24.984 11.052-25.169 24.84-.186 13.904 10.934 25.326 24.837 25.513l136.095 1.825 248.13 416.604c4.71 7.909 13.073 12.298 21.655 12.298.404 0 .808-.06 1.213-.08.404.019.809.08 1.213.08 8.581 0 16.945-4.391 21.655-12.298l248.129-416.604 136.096-1.825c13.904-.187 25.023-11.609 24.837-25.513-.189-13.904-11.567-24.951-25.516-24.838z' fill='${logoCodes[categoryColor]}'/><g fill='rgb(53,72,94)'><path d='M753.783 132.156h-67.167c-12.517 0-24.212 6.715-30.523 17.521l-149.027 255.22-149.938-255.123c-6.321-10.753-17.996-17.435-30.773-17.435l-67.117.894c-9.441.06-18.293 3.796-24.925 10.515-6.631 6.722-10.248 15.622-10.187 25.064.129 19.358 15.984 35.109 35.665 35.109l46.749-.708 170.152 289.524c6.322 10.753 17.998 17.435 30.472 17.435h.05c12.497-.02 24.172-6.732 30.472-17.521l169.221-289.805h46.877c19.489 0 35.346-15.857 35.346-35.344s-15.858-35.346-35.347-35.346zM855.051 132.381c-19.825 0-35.955 16.128-35.955 35.953s16.13 35.953 35.955 35.953 35.953-16.128 35.953-35.953-16.128-35.953-35.953-35.953zM157.065 132.381c-19.825 0-35.953 16.128-35.953 35.953s16.128 35.953 35.953 35.953 35.955-16.128 35.955-35.953-16.13-35.953-35.955-35.953z'/></g></svg>`" color="transparent" :class="['margin-auto', 'vc-block', 'my-8']" />
        <div :class="['align-center']">
          <patreon-button />
        </div>
        <search-bar />
        <vc-collapsible name="vc-controls-list" :color="categoryColor">
          <vc-collapsible-item
            v-for="(navitem, index) in navigation"
            has-vc-list
            :key="index"
            :label="navitem.label"
            :open="navitem.open"
            :icon="navitem.icon">
            <vc-list :divider="false">
              <vc-list-item
                v-for="(item, itemIndex) in nav(navitem.items, navitem.sort)"
                v-if="!item.hide"
                v-ripple
                :key="itemIndex"
                icon="chevron_right"
                tag="router-link"
                :to="item.path">
                <vc-list-item-primary-text v-html="item.label" />
              </vc-list-item>
            </vc-list>
          </vc-collapsible-item>
          <vc-collapsible-item
            has-vc-list
            label="Donate"
            icon="fa-dollar">
            <vc-list :divider="false">
              <vc-list-item
                tag="a"
                v-ripple
                :to="item.to"
                target="_blank"
                icon="chevron_right"
                v-for="(item, itemIndex) in $store.state.config.donationLinks"
                :key="itemIndex">
                <vc-list-item-primary-text v-html="item.label" />
              </vc-list-item>
            </vc-list>
          </vc-collapsible-item>
          <vc-collapsible-item
            has-vc-list
            label="Links & Social"
            icon="insert_link">
            <vc-list :divider="false">
              <vc-list-item
                tag="a"
                v-ripple
                :to="item.to"
                target="_blank"
                icon="chevron_right"
                v-for="(item, itemIndex) in $store.state.config.links"
                :key="itemIndex">
                <vc-list-item-primary-text v-html="item.label" />
              </vc-list-item>
            </vc-list>
          </vc-collapsible-item>
          <vc-collapsible-item
            has-vc-list
            label="Resources"
            icon="fa-database">
            <vc-list :divider="false">
              <vc-list-item
                tag="a"
                v-ripple
                :to="item.to"
                target="_blank"
                icon="chevron_right"
                v-for="(item, itemIndex) in $store.state.config.resources"
                :key="itemIndex">
                <vc-list-item-primary-text v-html="item.label" />
              </vc-list-item>
            </vc-list>
          </vc-collapsible-item>
        </vc-collapsible>
      </vc-container>
    </vc-nav-drawer>
    <vc-toolbar fixed :color="categoryColor" tag="header" application :class="['app-bar']">
      <vc-toolbar-icon-left icon="menu" @click="open = !open" />
      <vc-toolbar-title>{{ subtitle }}</vc-toolbar-title>
      <toolbar-links :color="categoryColor" :class="['sm-hide']" />
    </vc-toolbar>
    <vc-container tag="main" application>
      <vc-layout :spacing="8">
        <vc-col :span="24">
          <div :class="['vc-table-layout', 'breadrcrumbs-bar']">
            <div>
              <div :class="['api-link']" v-if="hasApi">
                <a href="#api-grid" :class="[`font-${categoryColor}`]">DOCS</a>
              </div>
              <div>
                <vc-breadcrumbs align="right" tag="router-link" :upper-case="true" :color="categoryColor" :links="breadcrumbs" />
              </div>
            </div>
          </div>
          <router-view />
          <support-panel />
        </vc-col>
      </vc-layout>
    </vc-container>
    <vc-footer v-if="footerVisible" narrow application tag="footer" ref="footer" :color="categoryColor" align="right">
      &copy; {{ year }} <router-link to="/">Vuecidity</router-link> by <a href="https://wemakesites.net" target="_blank">Martin Ivanov</a> | <a href="https://wemakesites.net/contact" target="_blank">Contact</a> | {{ description }}
    </vc-footer>
    <vc-dialog modal :color="categoryColor" :show="codeDialog" @close="codeDialog = false" :width="codeDialogWidth">
      <vc-dialog-header :close-button="true" icon="code">{{ codeDialogTitle }}</vc-dialog-header>
      <vc-dialog-content :max-height="codeDialogContentMaxHeight" style="min-height: 70px">
        <ajax-panel v-if="codeDialogSrc" :src="codeDialogSrc" inline-template>
          <div :class="['ajax-panel']"><pre><code>{{ template }}</code></pre></div>
        </ajax-panel>
        <vc-loading-overlay :visible="exampleLoading" :spinnerColor="categoryColor" spinnerSize="medium" />
      </vc-dialog-content>
      <vc-dialog-footer>
        <vc-button :color="categoryColor" @click="codeDialog = false">OK</vc-button>
      </vc-dialog-footer>
    </vc-dialog>
    <api-grid ref="api-grid" />
    <vc-loading-overlay application :visible="overlayVisible" spinnerSize="large" spinnerIcon="mdi-loading" :spinnerColor="categoryColor" />
    <vc-cookie-consent application :fixed="true" :cookie-days="7" :buttons-dark="true" :buttons-color="categoryColor" read-button-tag="router-link" read-button-to="/cookie-policy">I use cookies to ensure you get the best experience</vc-cookie-consent>
    <vc-social-bar size="small" expand-on-hover :button-color="categoryColor" :fixed="true" :items="social"/>
  </div>
</template>
<script>
import methods from './helpers/methods'
import Social from '../static/examples/components/social-bar/social'

export default {
  name: 'App',
  mixins: [
    methods
  ],
  data () {
    return {
      version: null,
      footerVisible: true,
      open: true,
      fixed: true,
      social: Social,
      logoCodes: {
        cyan: 'rgb(85,186,209)',
        indigo: 'rgb(63,83,175)',
        pink: 'rgb(213,57,100)',
        primary: 'rgb(69,150,236)',
        teal: 'rgb(68,147,136)',
        purple: 'rgb(141,55,170)'
      },
      hasApi: false,
      temporary: false,
      overlayVisible: false,
      exampleLoading: false,
      codeDialog: false,
      codeDialogSrc: null,
      codeDialogTitle: null,
      codeDialogWidth: null,
      codeDialogContentMaxHeight: null,
      breadcrumbs: [],
      title: null,
      subtitle: null,
      description: null,
      routes: this.$router.options.routes,
      year: new Date().getFullYear(),
      navigation: [{
        label: 'Getting Started',
        items: 'getting-started',
        icon: 'hourglass_empty',
        color: 'primary',
        open: location.href.includes('getting-started') || location.pathname === '/',
        sort: false
      }, {
        label: 'Layout',
        items: 'layout',
        icon: 'mdi-monitor',
        color: 'pink',
        open: location.href.includes('layout') || location.pathname === '/layout',
        sort: true
      }, {
        label: 'Styles and Colors',
        items: 'style',
        icon: 'style',
        color: 'teal',
        open: location.href.includes('style') || location.pathname === '/style',
        sort: true
      }, {
        label: 'UI Components',
        items: 'components',
        icon: 'mdi-view-dashboard',
        color: 'indigo',
        open: location.href.includes('components') || location.pathname === '/components',
        sort: true
      }, {
        label: 'Form Elements',
        items: 'form-elements',
        icon: 'panorama_wide_angle',
        color: 'cyan',
        open: location.href.includes('form-elements') || location.pathname === '/form-elements',
        sort: true
      }, {
        label: 'Directives',
        items: 'directives',
        icon: 'functions',
        color: 'purple',
        open: location.href.includes('directives') || location.pathname === '/directives',
        sort: true
      }],
      hiddenFooter: [
        '/getting-started/sponsors',
        '/getting-started/support-vuecidity',
        '/layout/spacing',
        '/style/theme',
        '/directives',
        '/layout',
        '/search'
      ]
    }
  },
  computed: {
    categoryColor () {
      return this.$store.getters.categoryColor
    }
  },
  watch: {
    '$route' () {
      this.codeDialog = false
      this.codeDialogSrc = null
      this.codeDialogTitle = null
      this.setCategoryColor()
      this.setBreadcrumbs()
      this.toggleFooter()
    }
  },
  created () {
    const root = this.$root
    const events = this.$store.state.events
    this.setCategoryColor()
    this.setBreadcrumbs()
    root.$on(events.ready, (data) => {
      this.title = data.title
      this.subtitle = data.subtitle
      this.description = data.description
    })
    root.$on(events.pageReady, (data) => {
      this.meta(data)
    })
    root.$on(events.hasApi, (data) => {
      this.hasApi = data
    })
    root.$on(events.showCodeDialog, (data) => {
      const codeDialogSrc = `/static/examples${this.$route.path}/${data.src}`
      const codeDialogWidth = document.documentElement.clientWidth * 70 / 100
      const codeDialogContentMaxHeight = document.documentElement.clientHeight * 40 / 100
      this.codeDialog = true
      this.codeDialogSrc = codeDialogSrc
      this.codeDialogTitle = data.title
      this.codeDialogWidth = `${codeDialogWidth}px`
      this.codeDialogContentMaxHeight = `${codeDialogContentMaxHeight}px`
    })
    root.$on(events.ajaxStart, () => {
      this.overlayVisible = true
    })
    root.$on(events.ajaxEnd, () => {
      this.overlayVisible = false
    })
    root.$on(events.exampleLoading, () => {
      this.exampleLoading = true
    })
    root.$on(events.exampleLoaded, () => {
      this.exampleLoading = false
    })
    this.$http(this.$store.state.npmVersion).then(response => {
      if (response.data && response.data.versions) {
        this.$store.commit('setVuecidityVersion', Object.keys(response.data.versions).pop() || null)
        this.version = this.$store.getters.vuecidityVersion
      }
    })
    this.toggleFooter()
  },
  methods: {
    toggleFooter () {
      this.footerVisible = this.hiddenFooter.indexOf(this.$route.path) === -1
    },
    resize () {
      const appElements = document.querySelectorAll('.vc-application:not(.vc-nav-drawer):not(.vc-loading-overlay)')
      const width = this.viewport().width
      if (width <= 960) {
        this.open = false
        this.temporary = true
        this.fixed = false
        appElements.forEach(element => {
          element.removeAttribute('style')
        })
      } else if (this.viewport().width >= 961) {
        this.open = true
        this.temporary = false
        this.fixed = true
      }
    },
    setBreadcrumbs () {
      const parts = window.location.pathname.split('/')
      const breadcrumbs = []
      if (this.$route.path === '/') {
        this.breadcrumbs = []
        return
      }
      parts.forEach((part, index) => {
        breadcrumbs.push({
          title: part === '' ? 'Home' : part.replace(/-/g, ' '),
          to: index === 0 ? '/' : index === 1 ? '/' + part : '/' + parts[index - 1] + '/' + part
        })
      })
      this.breadcrumbs = breadcrumbs
    },
    meta (data) {
      const d = document
      const siteDetails = this.$store.getters.siteDetails
      d.title = `${data.title || this.getLangKey('noServerSideRenderingYet')} - ${siteDetails.title} - ${siteDetails.subtitle}`
      d.querySelector('[name="keywords"]').setAttribute('content', data.keywords ? data.keywords.join(',') : '')
      d.querySelector('[name="description"]').setAttribute('content', data.description || '')
    },
    nav (filter, sort) {
      const routes = this.routes.filter(route => route.type === filter)
      if (!sort) {
        return routes
      }
      return routes.sort((a, b) => {
        if (a.name < b.name) {
          return -1
        }
        if (a.name > b.name) {
          return 1
        }
        return 0
      })
    }
  }
}
</script>
<style>
.breadrcrumbs-bar.vc-table-layout
{
  table-layout: auto !important;
}

.breadrcrumbs-bar.vc-table-layout .api-link
{
  width: 38px;
}
</style>



// WEBPACK FOOTER //
// src/App.vue